<template>
  <v-wait :for="loaderId">
    <template slot="waiting">
      <half-circle-spinner />
      <div class="spinner-message my-3 text-center">
        Loading data...
      </div>
    </template>
    <b-row>
      <b-col cols="12">
        <div class="font_size_1250rem my-2 v2_font_weight_semibold">
          <p>Select the state</p>
        </div>
      </b-col>
    </b-row>
    <div class="state-select-container">
    <b-row>
      <b-col cols="12" md="6" sm="6" class="my-2 built-in-query">
        <b-form-group
          label="State:"
          label-class="v2_font_weight_semibold"
        >
          <div class="v2_phairify-select-wrapper">
            <b-form-select
              class="phairify-border-grey state-select"
              :value="selectedState"
              :options="stateOptions"
              :state="validateState('selectedState')"
              @change="onStateChange"
            >
              <template slot="first">
                <option :value="null" disabled>
                  -- Select --
                </option>
              </template>
            </b-form-select>
          </div>
          <div
            v-show="$v.selectedState.$anyError"
            tabindex="-1"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            class="invalid-feedback"
          >
            This field is required
          </div>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6" sm="6" class="my-2 built-in-query">
        <v-wait :for="loaderCitiesId">
          <template slot="waiting">
            <half-circle-spinner />
            <div class="spinner-message my-3 text-center">
              Loading the cities...
            </div>
          </template>
          <b-form-group
            label="City (Optional):"
            label-class="v2_font_weight_semibold "
          >
            <div class="v2_phairify-select-wrapper">
              <b-form-select
                class="phairify-border-grey state-select"
                :value="selectedCityZip"
                :options="zipCodesOptions"
                :disabled="!selectedState"
                :state="selectedCityZip ? validateState('selectedCity') : null"
                @change="onCityChange"
              >
                <template slot="first">
                  <option :value="null">
                    -- Select --
                  </option>
                </template>
              </b-form-select>
            </div>
          </b-form-group>
        </v-wait>
      </b-col>
    </b-row>
    </div>
  </v-wait>
</template>

<script>
import { validationMixin } from 'vuelidate';
import validateStateMixin from '@/mixins/validateStateMixin';
import { required } from 'vuelidate/lib/validators';
import { HalfCircleSpinner } from 'epic-spinners';
import Api from '@/providers/Api';
import { GEOGRAPHIC_PREFERENCE } from '@/helpers/jobQuestionsHelper';
import SubFormValidationMethodsMixin from '@/mixins/SubFormValidationMethodsMixin';

export default {
  name: 'StateGeographyForm',
  mixins: [validationMixin, validateStateMixin, SubFormValidationMethodsMixin],
  props: {
    preferenceData: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  components: {
    HalfCircleSpinner,
  },
  data: function () {
    return {
      states: [],
      zipCodes: [],
    };
  },
  validations: {
    selectedState: {
      required,
    },
    selectedCity: {},
  },
  created: function () {
    this.getStates();
    if (this.selectedState) {
      this.getZipCodeOptions();
    }
  },
  computed: {
    stateOptions: function () {
      return this.states.map(({ state_id, state_name }) => ({
        value: state_id,
        text: state_name,
      }));
    },
    zipCodesOptions: function () {
      if (!this.selectedState) return [];
      return this.zipCodes
        .filter(({ state_id }) => state_id === this.selectedState)
        .map(({ zip, city }) => ({ value: zip, text: city }));
    },
    selectedState: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.state_id
        : null;
    },
    selectedCityZip: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.city_id
        : null;
    },
    currentDisplayName: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.display_name
        : null;
    },
    loaderId: function () {
      return `${this.$options.name}_loading`;
    },
    loaderCitiesId: function () {
      return `${this.$options.name}_cities_loading`;
    },
  },
  methods: {
    getStates: async function () {
      this.$wait.start(this.loaderId);
      try {
        this.states = await Api.getStates();
      } catch (e) {
        this.$toasted.error('Unable to upload states data', {
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    onStateChange: function (selected) {
      const { text: displayName = null } =
        this.stateOptions.find(({ value }) => selected === value) || {};
      this.$emit('set-preference', {
        type: GEOGRAPHIC_PREFERENCE.STATE,
        value: {
          state_id: selected,
          city_id: null,
          display_name: displayName,
        },
      });
      this.$v.selectedState.$touch();
    },
    onCityChange: function (selected) {
      const { text: displayName = this.currentDisplayName } =
        this.zipCodesOptions.find(({ value }) => selected === value) || {};
      this.$emit('set-preference', {
        type: GEOGRAPHIC_PREFERENCE.STATE,
        value: {
          state_id: this.selectedState,
          city_id: selected,
          display_name: displayName,
        },
      });
      this.$v.selectedCity.$touch();
      this.getZipCodeOptions();
    },
    getZipCodeOptions: async function () {
      if (this.zipCodesOptions.length) return;

      this.$wait.start(this.loaderCitiesId);
      try {
        const cities = await Api.getCities(this.selectedState);
        this.zipCodes.push(...cities);
      } catch (e) {
        this.$toasted.error('Unable to upload cities data', {
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.loaderCitiesId);
      }
    },
  },
  watch: {
    selectedState: function () {
      this.onCityChange(null);
    },
  },
};
</script>

<style lang="scss" scoped>
.invalid-feedback {
  display: block;
}
.state-select-container {
  border: 1px solid grey;
  padding: 15px 25px;
}
.state-select {
  height: 40px;
  padding: 0 5px;
}
@media (min-width:768px) and (max-width:1330px) {
  .built-in-query {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
  .state-select-container{
    padding: 15px 15px !important;
  }
}
</style>
